import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchSubscribeTerms, requestSubscription } from '../newsApi';
import { formatDayLabel, NEWS_SUBSCRIBED_KEY } from '../newsFormat';

const RESULT_TEXT = {
  active: (start) => ({ title: '구독했어요', body: `${formatDayLabel(start)} 아침 8시부터 메일로 보내드릴게요.` }),
  already: () => ({ title: '이미 구독 중이에요', body: '매일 아침 8시에 메일로 보내드리고 있어요.' }),
  pending: () => ({ title: '확인 메일을 보냈어요', body: "메일의 '구독 확정하기' 버튼을 누르면 다음 날 아침부터 보내드려요." }),
  cooldown: () => ({ title: '방금 확인 메일을 보냈어요', body: '메일함(스팸함 포함)에서 확인 메일을 찾아 주세요.' }),
};

const ConsentRow = ({ id, checked, onChange, label, detail }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="news-sub-consent">
      <label htmlFor={id} className="news-sub-check">
        <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>{label}</span>
      </label>
      {detail && (
        <button type="button" className="news-sub-detail-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? '접기' : '내용 보기'}
        </button>
      )}
      {open && detail && <p className="news-sub-detail">{detail}</p>}
    </div>
  );
};

const NewsSubscribe = () => {
  const { currentUser } = useAuth() || {};
  const [email, setEmail] = useState('');
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [terms, setTerms] = useState(null);
  const [status, setStatus] = useState({ phase: 'idle', result: null, start: null, error: '' });

  useEffect(() => {
    if (currentUser?.email) setEmail((prev) => prev || currentUser.email);
  }, [currentUser]);

  useEffect(() => {
    fetchSubscribeTerms().then(setTerms).catch(() => setTerms(null));
  }, []);

  const canSubmit = /\S+@\S+\.\S+/.test(email) && privacy && marketing && status.phase !== 'sending';
  const isAccountEmail = Boolean(currentUser?.emailVerified)
    && email.trim().toLowerCase() === (currentUser?.email || '').toLowerCase();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setStatus({ phase: 'sending', result: null, start: null, error: '' });
    try {
      const idToken = currentUser ? await currentUser.getIdToken() : null;
      const { ok, status: code, data } = await requestSubscription(email.trim(), idToken);
      if (ok) {
        if (data.result === 'active' || data.result === 'already') {
          try {
            window.localStorage.setItem(NEWS_SUBSCRIBED_KEY, '1');
          } catch (_) { /* 채팅 화면의 '메일로 받기'를 숨기는 용도라 실패해도 괜찮다 */ }
        }
        setStatus({ phase: 'done', result: data.result, start: data.start_date, error: '' });
        return;
      }
      const error = code === 429
        ? '신청이 많아요. 잠시 후 다시 시도해 주세요.'
        : (code === 400 && data.detail) || '지금 신청하지 못했어요. 잠시 후 다시 시도해 주세요.';
      setStatus({ phase: 'idle', result: null, start: null, error });
    } catch (_) {
      setStatus({ phase: 'idle', result: null, start: null, error: '네트워크 연결을 확인해 주세요.' });
    }
  };

  if (status.phase === 'done') {
    const text = (RESULT_TEXT[status.result] || RESULT_TEXT.pending)(status.start);
    return (
      <section id="subscribe" className="news-sub news-sub--done" aria-live="polite">
        <div className="news-sub-check-icon" aria-hidden="true">✓</div>
        <h2 className="news-sub-title">{text.title}</h2>
        <p className="news-sub-desc">{text.body}</p>
      </section>
    );
  }

  return (
    <section id="subscribe" className="news-sub" aria-labelledby="news-sub-title">
      <h2 id="news-sub-title" className="news-sub-title">매일 아침 8시, 메일로 받아 보세요</h2>
      <p className="news-sub-desc">
        {isAccountEmail
          ? '로그인한 이메일이라 확인 메일 없이 바로 신청돼요. 다음 날 아침부터 보내드려요.'
          : '출근길 3분이면 다 읽어요. 신청하면 다음 날 아침부터 보내드려요.'}
      </p>
      <form onSubmit={handleSubmit} noValidate>
        <label htmlFor="news-sub-email" className="news-sub-label">이메일 주소</label>
        <input
          id="news-sub-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          className="news-sub-input"
          placeholder="teacher@school.kr"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={254}
        />
        <ConsentRow
          id="news-sub-privacy"
          checked={privacy}
          onChange={setPrivacy}
          label="[필수] 개인정보 수집·이용 동의"
          detail={terms?.privacy}
        />
        <ConsentRow
          id="news-sub-marketing"
          checked={marketing}
          onChange={setMarketing}
          label="[필수] 광고성 정보(이메일) 수신 동의"
          detail={terms?.marketing}
        />
        {status.error && <p className="news-sub-error" role="alert">{status.error}</p>}
        <button type="submit" className="news-btn-primary news-sub-submit" disabled={!canSubmit}>
          {status.phase === 'sending' ? '신청하는 중' : '메일로 받기'}
        </button>
      </form>
    </section>
  );
};

export default NewsSubscribe;
